import { LogIn, ShieldCheck, Zap } from 'lucide-react'
import { Navigate } from 'react-router'

import driverCabImg from '@/assets/marketing/images/large-fleets-driver-cab.jpg'
import fleetHeroImg from '@/assets/marketing/images/large-fleets-hero.jpg'
import { Anchor } from '@/components/ui/Anchor'
import { Button } from '@/components/ui/button'
import { appHref } from '@/config/config'
import { trackMarketingCta } from '@/lib/analytics'
import { useHostGuard } from '@/lib/hostGuard'
import { MarketingCarrierLogos } from './components/MarketingCarrierLogos'
import { NavBar } from './components/NavBar'
import { Reveal } from './components/Reveal'
import { SiteFooter } from './components/SiteFooter'
import { ValueProp } from './components/ValueProp'

/* Standalone large-fleet insurance landing page — own header and footer, no MarketingLayout, same pattern as Bookkeeper.tsx. */

// Campaign traffic funnels into the existing large-fleet packet upload, not the standard quote flow.
const QUOTE_HREF = appHref('/insurance/large-fleets')

const PHONE_NUMBER = '(770) 765-0331'

const COVERAGES = [
  'Commercial Auto Liability',
  'Cargo',
  'Physical Damage',
  'General Liability',
  'Workers Compensation',
  'Cyber',
]

const VALUE_PROPS = [
  {
    key: 'competitive-quotes',
    icon: <Zap />,
    title: 'Competitive quotes, fast',
    body: 'We work with top insurance providers to find the best quotes for your business.',
  },
  {
    key: 'safety-compliance',
    icon: <ShieldCheck />,
    title: 'Year-round safety & compliance support',
    body: 'We flag risks before they hit your premiums, not after.',
  },
  {
    key: 'policy-management',
    icon: <LogIn />,
    title: 'Seamless policy management',
    body: 'We make it easy to get what you need to keep moving.',
  },
]

export function LargeFleets() {
  const { redirecting, navigateTo } = useHostGuard('marketing')
  if (navigateTo) return <Navigate to={navigateTo} replace />
  if (redirecting) return null

  return (
    <div className="bg-ink-50 min-h-screen w-full shrink-0">
      <title>Cai | Insurance for large fleets</title>
      <meta
        name="description"
        content="Trucking insurance that keeps up with your fleet. Fast quoting, instant COIs, and easy policy management from a broker that used to run its own fleet."
      />

      <NavBar
        theme="light"
        phoneNumber={PHONE_NUMBER}
        onPhoneClick={() => trackMarketingCta('large_fleets_nav_phone')}
        utilityLinks={[
          {
            label: 'Login',
            icon: <LogIn />,
            href: appHref('/insure'),
          },
        ]}
        ctaLabel="Get your quotes"
        ctaHref={QUOTE_HREF}
        ctaNeutralOverSelector="#large-fleets-hero"
        onCtaClick={() => trackMarketingCta('large_fleets_nav_get_quote', { destination: QUOTE_HREF })}
      />

      <section id="large-fleets-hero" className="bg-ink-900 relative overflow-hidden">
        <img
          src={fleetHeroImg}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
        />
        {/* Only thins out at lg, where max-w holds the copy inside the dark end; narrower, it reflows wide over the trucks and needs the cover. */}
        <div className="from-ink-900/95 via-ink-900/85 to-ink-900/80 lg:to-ink-900/40 absolute inset-0 bg-gradient-to-r" />
        <div className="relative mx-auto max-w-(--maxw) px-5.5 py-24 lg:py-32">
          <Reveal className="flex max-w-225 flex-col gap-6.5">
            <span className="text-brand-500 font-mono text-xs font-bold tracking-[0.14em] uppercase">
              Insurance for large fleets
            </span>
            <h1 className="h1-hero max-w-[19ch] text-pretty text-white">
              Trucking insurance that keeps up with your fleet
            </h1>
            <p className="max-w-190 text-xl leading-[1.55] text-pretty text-white/80">
              We used to run our own fleet. Then we built the insurance experience we wished we had: fast
              quoting, instant COIs, and easy policy management.
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-5">
              <Button
                size="lg"
                href={QUOTE_HREF}
                onClick={() => trackMarketingCta('large_fleets_hero_get_quote', { destination: QUOTE_HREF })}
              >
                Get your quotes
              </Button>
              <span className="text-ink-300 text-md">No requirement to buy</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-ink-100 border-b bg-white">
        <MarketingCarrierLogos heading="Shop the nation's leading commercial insurance providers" />
      </section>

      <section className="bg-ink-50 px-5.5 py-24 lg:py-32">
        <div className="mx-auto max-w-(--maxw)">
          <div className="grid grid-cols-2 items-stretch gap-14 max-lg:grid-cols-1">
            <Reveal className="flex flex-col gap-5.5">
              <h2 className="h2 text-pretty">
                Upload your documents.
                <br />
                We take it from there.
              </h2>
              <p className="text-ink-600 max-w-115 text-lg leading-[1.6]">
                No long forms or questionnaires. Just your driver list, equipment list, and loss run reports
                if you have them, and our licensed brokers will get to work.
              </p>
              <div className="mt-1.5 flex">
                <Button
                  size="lg"
                  href={QUOTE_HREF}
                  onClick={() =>
                    trackMarketingCta('large_fleets_upload_get_started', { destination: QUOTE_HREF })
                  }
                >
                  Upload your documents
                </Button>
              </div>
            </Reveal>
            <Reveal
              delay={150}
              className="relative min-h-75 self-stretch overflow-hidden rounded-sm max-lg:min-h-[clamp(280px,42vw,380px)]"
            >
              {/* Positioned, not h-full: a percentage height against a min-height-only grid item resolves in Chrome but not Safari, where the image fell back to its intrinsic width. */}
              <img
                src={driverCabImg}
                alt="A driver relaxed in the cab with the sun coming through the windshield"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </Reveal>
          </div>
          <Reveal delay={100}>
            <div className="border-ink-200 mt-9 border-t pt-9">
              <span className="text-ink-500 font-mono text-xs font-bold tracking-[0.12em] uppercase">
                Coverages we shop
              </span>
              <div className="text-ink-500 mt-4.5 flex flex-wrap gap-x-7 gap-y-3 font-mono text-sm font-medium tracking-[0.04em]">
                {COVERAGES.map((coverage) => (
                  <span key={coverage}>{coverage}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink-800 px-5.5 py-24 lg:py-32">
        <div className="mx-auto max-w-(--maxw)">
          <Reveal className="flex flex-col items-start gap-3.5">
            <h2 className="h2 text-pretty text-white">Intelligent trucking insurance</h2>
            <Anchor
              href="https://blog.cai.ai/how-cai-is-using-ai-to-fix-trucking-insurance"
              className="text-ink-300 border-b border-white/30 pb-1.5 text-lg no-underline transition-colors duration-150 hover:border-white hover:text-white"
            >
              Learn more about how Cai uses AI.
            </Anchor>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-11 grid grid-cols-3 border border-white/15 max-md:grid-cols-1">
              {VALUE_PROPS.map((prop) => (
                <ValueProp
                  key={prop.key}
                  onDark
                  icon={prop.icon}
                  title={prop.title}
                  className="border-r border-white/15 px-8 py-10 last:border-r-0 max-md:border-r-0 max-md:border-b max-md:last:border-b-0"
                >
                  {prop.body}
                </ValueProp>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white px-5.5 py-24 lg:py-28">
        <Reveal className="mx-auto flex max-w-(--maxw) flex-wrap items-center justify-between gap-8">
          <h2 className="h2 max-w-150 text-pretty">See what your fleet should be paying for insurance.</h2>
          <Button
            size="lg"
            href={QUOTE_HREF}
            onClick={() => trackMarketingCta('large_fleets_footer_get_quote', { destination: QUOTE_HREF })}
          >
            Get your quotes
          </Button>
        </Reveal>
      </section>

      <SiteFooter
        hideEdgeLines
        tagline="Financial technology to empower the businesses that build, move, and fix the world around us."
        linkColumns={[
          {
            heading: 'Company',
            links: [
              { label: 'About', href: '/#about' },
              { label: 'Blog', href: 'https://blog.cai.ai', external: true },
            ],
          },
          {
            heading: 'Products',
            links: [
              { label: 'Insurance', href: '/insurance' },
              { label: 'Large fleets', href: '/insurance/large-fleet-coverage' },
              { label: 'FuelApp', href: '/fuelapp' },
            ],
          },
        ]}
        legalLinks={[
          { label: 'Privacy Policy', href: '/privacy' },
          { label: 'Terms of Service', href: '/terms' },
        ]}
        note={
          <p>
            Insurance services provided by Cai Insurance Services, a service of Cai Labs, Inc. Agency License
            #6018835. Descriptions are general in nature only. Please refer to the terms and conditions of any
            policies offered or purchased. Insurance products offered are subject to application and
            underwriting requirements. Not all products available in all states.
          </p>
        }
      />
    </div>
  )
}
